<template>
  <div>
    <v-tooltip location="top" open-delay="700">
      <template v-slot:activator="{ props }">
        <div v-bind="typeof rootEntity !== 'object' && !store.state.isWorkflowDisabled ? props : {}">
          <v-combobox
            :model-value="rootEntity"
            @update:focused="onFocusChange"
            @update:modelValue="updateRootEntity"
            @update:menu="onMenuChange"
            @blur="onBlur"
            clear-on-select
            persistent-hint
            persistent-placeholder
            :hide-no-data="false"
            :no-data-text="openCreateStep ? '' : 'No entities available'"
            :menu-icon="rootEntity && typeof rootEntity === 'object' ? '$dropdown' : 'mdi-link'"
            density="compact"
            variant="outlined"
            :custom-filter="filterFunction"
            :items="workflow.dataModels || []"
            item-value="id"
            item-title="name"
            :disabled="store.state.isWorkflowDisabled"
            ref="combobox"
            :style="{ '--inputMinWidth': inputMinWidth + 'px' }"
          >
            <template #selection="{ item }">
              <v-chip
                v-if="rootEntity && typeof rootEntity === 'object'"
                density="compact"
                class="mr-2"
              >
                {{ item.title }}
              </v-chip>
              <span v-else-if="showCustomValue" class="text-no-wrap" ref="selectionElement">
                {{ item.title }}
              </span>
            </template>

            <template #item="{ item, props }">
              <v-list-item v-bind="props" v-if="!openCreateStep">
                {{ item.name }}
              </v-list-item>
            </template>

            <template #append-item>
              <div v-if="openCreateStep && !store.state.isWorkflowDisabled">
                <v-text-field
                  v-if="!store.state.isWorkflowDisabled"
                  autofocus
                  :rules="[maxNameLength]"
                  class="select-entity"
                  density="compact"
                  v-model="entityName"
                  :placeholder="openCreateStep ? 'Name the entity' : 'Search for entity'"
                  variant="outlined"
                  @keydown.stop
                />
                <v-textarea
                  rows="3"
                  auto-grow
                  class="no-border"
                  variant="outlined"
                  v-model="aiDescription"
                  placeholder="Description (optional)"
                  @keydown.stop
                />
              </div>
              <v-divider class="my-2"></v-divider>
              <v-list-item
                class="d-flex align-center ga-2"
                @click="onCreateEntity"
                :disabled="store.state.isWorkflowDisabled || (openCreateStep && (!entityName || hasMatchingEntity))"
              >
                {{ openCreateStep ? 'Create' : 'Create a new entity with AI' }}
                <v-icon color="#787878">mdi-creation</v-icon>
              </v-list-item>
            </template>
          </v-combobox>
        </div>
      </template>
      <span>Name not linked to an entity yet, click to select an entity to link it.</span>
    </v-tooltip>
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import { EventBus } from '@/event-bus/event-bus'
import { sanitizeCardDescription } from '@/utils/sanitize'
import useGenerateEntity from '@/composables/useGenerateEntity'

const { events, boundedContextId, cardIds } = defineProps({ events: Array, boundedContextId: String, cardIds: Array })

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])

const openCreateStep = ref(false)
const rootEntity = ref(null)
const previousRoot = ref(null)
const showCustomValue = ref(true)
const aiDescription = ref('')
const entityName = ref('')
const selectionElement = ref(null)
const inputMinWidth = ref(0)
const combobox = useTemplateRef('combobox')
const { generateEntity } = useGenerateEntity()

const hasMatchingEntity = computed(() => {
  return (workflow.value.dataModels ?? []).some((entity) => entity.name.toLowerCase() === entityName.value.toLowerCase())
})

const filterFunction = (_, query, item) => {
  return typeof rootEntity.value === 'string' && query === rootEntity.value
    ? true
    : item.title.toLowerCase().indexOf(query.toLowerCase()) > -1
}

const onFocusChange = (focused) => {
  if (!combobox.value) {
    return
  }

  if (focused) {
    const parentWidth = selectionElement.value?.offsetParent?.getBoundingClientRect()?.width - 11 || 0
    if (typeof rootEntity.value === 'string') {
      inputMinWidth.value = parentWidth
      combobox.value.search = rootEntity.value
    } else {
      inputMinWidth.value = 64
    }
  } else {
    inputMinWidth.value = 0
  }

  showCustomValue.value = !focused
}

const onMenuChange = (isOpen) => {
  if (!isOpen && openCreateStep.value) {
    openCreateStep.value = false
    entityName.value = ''
    aiDescription.value = ''
  }
}

const markAsEventRootEntity = async (selectedEntity, assignBoundedContext) => {
  const updatedSvgGraph = structuredClone(svgGraph.value)
  const isStringValue = !selectedEntity || typeof selectedEntity === 'string'

  events.forEach((event) => {
    updatedSvgGraph[event.id].requirementsJson = updatedSvgGraph[event.id].requirementsJson?.map((item) => {
      return {
        id: item.id,
        description: cardIds.includes(item.id)
          ? (isStringValue ? selectedEntity || '' : item.description)
          : item.description,
        releaseNo: item.releaseNo,
        sortkey: item.sortkey,
        cardTypeId: item.cardTypeId,
        done: item.done,
        boundedContext: item.boundedContext,
        creatorId: item.creatorId,
        version: item.version,
        created: item.created,
        entityId: cardIds.includes(item.id) ? (isStringValue ? null : selectedEntity.id) : item.entityId,
        cardinality: item.cardinality
      }
    })
  })

  await workflowApi.updateWorkflow({
    svgGraph: updatedSvgGraph,
    dataModels: assignBoundedContext
      ? (workflow.value.dataModels ?? []).map((entity) => entity.id === selectedEntity?.id ? { ...entity, boundedContext: boundedContextId } : entity)
      : undefined,
    workflow: workflow.value,
    updateNote: {
      action: 'updated root entity',
      target: ''
    }
  }, true)

  EventBus.$emit('refresh-entity-relationship-diagram')
}

const onBlur = async () => {
  if (previousRoot.value && !rootEntity.value) {
    await markAsEventRootEntity(null)
  }
}

const updateRootEntity = async (entity) => {
  // If the selected entity is not linked to a bounded context, and the entity card has bounded context selected
  // we need to assign the bounded context to the entity when marking it as root
  const assignBoundedContext = boundedContextId && typeof rootEntity.value === 'string' && !entity.boundedContext

  rootEntity.value = entity

  if (!entity) {
    return
  }

  if (combobox.value) {
    combobox.value.blur()
  }

  await markAsEventRootEntity(entity, assignBoundedContext)
}

const maxNameLength = (name) => {
  return name.length <= 30 || 'Name must be less than 30 characters'
}

const onCreateEntity = async () => {
  if (!openCreateStep.value) {
    openCreateStep.value = true
    if (combobox.value.search) {
      entityName.value = combobox.value.search
    }
    combobox.value.search = ''
    return
  }

  await generateEntity({
    eventId: events?.length === 1 ? events[0].id : null,
    name: entityName.value.trim(),
    description: aiDescription.value.trim()
  })

  openCreateStep.value = false
}

watch([() => workflow.value.dataModels, () => events], ([newEntities, newEvents]) => {
  const card = newEvents[0].requirementsJson?.find((item) => cardIds.includes(item.id))
  const entity = (newEntities || []).find((entity) => entity.id === card?.entityId) ||
    (card ? sanitizeCardDescription(card.description || '') : '')

  rootEntity.value = entity
  previousRoot.value = entity
}, { immediate: true })
</script>

<style>
.v-combobox {
  .v-field {
    &:not(.v-field--focused) {
      input {
        flex-grow: 0 !important;
        width: 0;
      }
    }
  }

  .v-field__input {
    flex-wrap: nowrap !important;
    min-height: 20px !important;
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    padding-left: 5px !important;

    .v-combobox__selection {
      max-width: 100%;
    }

    input {
      min-width: var(--inputMinWidth) !important;
    }
  }

  .v-field.v-field--variant-outlined {
    .v-field__outline__start,
    .v-field__outline__notch::before,
    .v-field__outline__notch::after,
    .v-field__outline__end {
      border: 1px solid var(--borderColor);
      border-top-width: var(--borderTopWidth);
      --v-field-border-opacity: 1;
    }

    .v-field__outline__start {
      border-right: none;
    }

    .v-field__outline__end {
      border-left: none;
    }
  }

  .v-input__details {
    display: none !important;
  }

  .v-combobox__selection {
    overflow: hidden;
  }
}

.v-combobox__content {
  .v-list {
    .v-list-item {
      padding: 4px 8px !important;
      min-height: auto;
    }
  }
}
</style>
